/**
 * Candidate Import Wizard
 *
 * Multi-step wizard for importing candidates from CVs.
 * Supports single CV and bulk CV (up to 50 files) import.
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stepper, Step, StepLabel, Paper } from "@mui/material";
import Step1_MethodSelection from "./Step1_MethodSelection";
import Step2_Upload from "./Step2_Upload";
import Step3_Parse from "./Step3_Parse";
import Step4_Review from "./Step4_Review";
import Step5_Confirm from "./Step5_Confirm";

export interface ParsedCandidate {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  location?: string;
  linkedin_url?: string;
  main_skill?: string;
  seniority?: string;
  role_type?: string;
  years_of_experience?: number;
  expected_rate?: number;
  notice_period?: string;
  english_level?: string;
  summary?: string;
  skills_data?: Record<string, number>;
  tags?: string[];
  _fileId?: string;
  _fileName?: string;
  _fileUrl?: string;
}

export interface BulkFileResult {
  index: number;
  fileName: string;
  status: "pending" | "processing" | "success" | "error";
  parsedCandidate?: ParsedCandidate | null;
  error?: string;
  fileId?: string;
  rawText?: string;
  included: boolean;
}

export interface WizardState {
  currentStep: 1 | 2 | 3 | 4 | 5;
  importMethod: "single-cv" | "bulk-cv";
  // Single mode
  uploadedFile?: File;
  parsedCandidate?: ParsedCandidate | null;
  parseError?: string | null;
  fileId?: string;
  rawText?: string;
  // Bulk mode
  uploadedFiles?: File[];
  bulkResults?: BulkFileResult[];
}

const CandidateImportWizard: React.FC = () => {
  const { t } = useTranslation(["recruiting"]);
  const steps = [
    t("import.wizard.steps.method"),
    t("import.wizard.steps.upload"),
    t("import.wizard.steps.parse"),
    t("import.wizard.steps.review"),
    t("import.wizard.steps.confirm"),
  ];
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 1,
    importMethod: "single-cv",
  });

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 5) as any);
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1) as any);
  };

  const updateWizardState = (updates: Partial<WizardState>) => {
    setWizardState((prev) => ({ ...prev, ...updates }));
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: 3 }}>
      <Stepper activeStep={currentStep - 1} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 3, minHeight: 400 }}>
        {currentStep === 1 && (
          <Step1_MethodSelection
            onNext={handleNext}
            updateState={updateWizardState}
          />
        )}
        {currentStep === 2 && (
          <Step2_Upload
            wizardState={wizardState}
            onNext={handleNext}
            onBack={handleBack}
            updateState={updateWizardState}
          />
        )}
        {currentStep === 3 && (
          <Step3_Parse
            wizardState={wizardState}
            onNext={handleNext}
            onBack={handleBack}
            updateState={updateWizardState}
          />
        )}
        {currentStep === 4 && (
          <Step4_Review
            wizardState={wizardState}
            onNext={handleNext}
            onBack={handleBack}
            updateState={updateWizardState}
          />
        )}
        {currentStep === 5 && (
          <Step5_Confirm wizardState={wizardState} onBack={handleBack} />
        )}
      </Paper>
    </Box>
  );
};

export default CandidateImportWizard;
